import { DAILY_MARKET_COPY } from "@/src/components/DailyMarketSummary/constants/copy";
import type {
  DailyMarketMode,
  DailyMarketReportDTO,
} from "@/src/components/DailyMarketSummary/types";
import type { IndicesItemDataType } from "@/src/components/Home/IndicesList/type";

/** Protocol: ask-ed-integration-protocol.md §2 — keep referenceSource concise. */
export const DAILY_MARKET_ASK_ED_MAX_REFERENCE_SOURCE_CHARS = 1000;

export const DAILY_MARKET_ASK_ED_DEFAULT_USER_QUESTION =
  "What should I know about today's market summary?";

export const DAILY_MARKET_ASK_ED_BRACKET_LABEL = "Daily market summary";

export interface BuildDailyMarketAskEdQuestionInput {
  mode: DailyMarketMode;
  summary: DailyMarketReportDTO | null;
  personalContent?: string;
  isPersonalGenerating?: boolean;
  indices?: IndicesItemDataType[];
  userQuestion?: string;
}

export function formatDailyMarketReportDate(date: string): string {
  if (/^\d{8}$/.test(date)) {
    return `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`;
  }
  return date;
}

export function formatDailyMarketIndexLine(item: IndicesItemDataType): string {
  const pct =
    typeof item.change_percentage === "number"
      ? `${item.change_percentage >= 0 ? "+" : ""}${item.change_percentage.toFixed(2)}%`
      : String(item.change_percentage);
  const price = item.value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `- ${item.index} (${item.symbol}): $${price} (${pct})`;
}

export function resolveDailyMarketAskEdBody(
  input: BuildDailyMarketAskEdQuestionInput,
): { body: string; usingPersonal: boolean } {
  const personalReady =
    input.mode === "personal" &&
    !input.isPersonalGenerating &&
    Boolean(input.personalContent?.trim());

  if (personalReady) {
    return { body: input.personalContent!.trim(), usingPersonal: true };
  }

  return {
    body: input.summary?.content?.trim() ?? "",
    usingPersonal: false,
  };
}

export function buildDailyMarketReferenceSource(
  input: BuildDailyMarketAskEdQuestionInput,
): string {
  const { body, usingPersonal } = resolveDailyMarketAskEdBody(input);
  const cardTitle = usingPersonal
    ? DAILY_MARKET_COPY.personalTitle
    : input.summary?.title?.trim() || DAILY_MARKET_COPY.eyebrow;

  const lines = [
    "Brief Type: Daily market summary",
    `Card Title: ${cardTitle}`,
  ];

  if (input.summary?.date) {
    lines.push(`Date: ${formatDailyMarketReportDate(input.summary.date)}`);
  }

  if (input.indices && input.indices.length > 0) {
    lines.push("", "Market Indices:");
    lines.push(...input.indices.map(formatDailyMarketIndexLine));
  }

  if (body) {
    lines.push("", usingPersonal ? "Report Content:" : "Key Insight:", body);
  }

  let source = lines.join("\n");
  if (source.length > DAILY_MARKET_ASK_ED_MAX_REFERENCE_SOURCE_CHARS) {
    source =
      source.slice(0, DAILY_MARKET_ASK_ED_MAX_REFERENCE_SOURCE_CHARS - 3) +
      "...";
  }

  return source;
}

export function encodeDailyMarketReferenceSource(value: string): string {
  // encodeURIComponent leaves ( ) unescaped; protocol examples use %28/%29 inside markdown links.
  return encodeURIComponent(value).replace(/\(/g, "%28").replace(/\)/g, "%29");
}

/**
 * Ask Ed — direct_context per ask-ed-integration-protocol.md §3 (Daily Market / Scene C).
 */
export function buildDailyMarketAskEdQuestion(
  input: BuildDailyMarketAskEdQuestionInput,
): string {
  const referenceSource = buildDailyMarketReferenceSource(input);
  const userQuestion =
    input.userQuestion?.trim() || DAILY_MARKET_ASK_ED_DEFAULT_USER_QUESTION;
  const encoded = encodeDailyMarketReferenceSource(referenceSource);

  return `[${DAILY_MARKET_ASK_ED_BRACKET_LABEL}](referenceType=direct_context&referenceSource=${encoded})\n${userQuestion}`;
}
